/* The campaign name, made from the label a person typed.
 *
 * A campaign has two names and until now a person had to type both. The label
 * is the words ("California, large multifamily"). The name is the slug that
 * jobs, exports and every other api function refer to
 * ("california-large-multifamily"), and api.save_criteria_set refuses anything
 * that is not lowercase letters, digits and hyphens:
 *
 *     ^[a-z0-9][a-z0-9-]{1,62}$
 *
 * So the slug is derived here and the person corrects it if they want to,
 * rather than being asked for a second name in a vocabulary that belongs to
 * the database.
 *
 * Why the name stops being editable once a campaign is saved.
 * api.save_criteria_set is an upsert ON CONFLICT (name). Changing the name of
 * a saved campaign therefore writes a second campaign and leaves the first one
 * where it was, with its runs and its export history still attached to the old
 * name. That is not a rename, and offering it as one would lose a campaign in
 * plain sight.
 */

/** The longest name api.save_criteria_set accepts. */
export const SLUG_MAX = 63;
/** The shortest, because the pattern needs a first character and one more. */
export const SLUG_MIN = 2;

export function campaignSlug(label: string): string {
  const folded = label
    // Accents off first, so Peña becomes pena rather than pe-a.
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    // Anything that is not a letter or a digit becomes one hyphen. That covers
    // spaces, an em dash, a colon and an ampersand in one rule.
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (folded.length <= SLUG_MAX) {
    return folded.length >= SLUG_MIN ? folded : "";
  }
  // Cut to the ceiling, then back to a word boundary so a truncated name does
  // not end mid-word or on a hyphen.
  const cut = folded.slice(0, SLUG_MAX).replace(/-+[^-]*$/, "").replace(/-+$/, "");
  const kept = cut.length >= SLUG_MIN ? cut : folded.slice(0, SLUG_MAX).replace(/-+$/, "");
  return kept.length >= SLUG_MIN ? kept : "";
}

/** What is wrong with a name, in words, or null when it is fine. */
export function slugProblem(name: string): string | null {
  if (!name) return "A campaign needs a name. Type a label and one is made for you.";
  if (name.length < SLUG_MIN) return "That name is too short.";
  if (name.length > SLUG_MAX) return `That name is over ${SLUG_MAX} characters.`;
  if (!/^[a-z0-9][a-z0-9-]*$/.test(name)) {
    return "A name can use lowercase letters, digits and hyphens, and has to start with a letter or a digit.";
  }
  return null;
}
